import { ClipboardPen } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from '@/components/feedback/toast-store'
import { FormField } from '@/components/forms/FormField'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { ToggleRow } from './ToggleRow'
import { Textarea } from '@/components/ui/Textarea'
import { isoDate } from '@/features/attendance/dates'
import { useSaveAssignment } from '../api'
import type { Assignment } from '../types'
import { AttachmentField, type Attached } from './AttachmentField'
import { useFieldErrors } from './useFieldErrors'

const FIELDS = ['title', 'instructions', 'dueAt', 'maxPoints', 'fileIds'] as const

function defaultDue(): string {
  const d = new Date()
  d.setDate(d.getDate() + 7)
  return `${isoDate(d)}T18:00`
}

/** Create or edit homework: instructions, due date, optional points and late policy. */
export function AssignmentDialog({
  sectionId,
  subjectId,
  assignment,
  onOpenChange,
}: {
  sectionId: number
  subjectId: number
  assignment?: Assignment
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('lms')
  const save = useSaveAssignment(sectionId, subjectId)
  const [title, setTitle] = useState(assignment?.title ?? '')
  const [instructions, setInstructions] = useState(assignment?.instructions ?? '')
  const [dueAt, setDueAt] = useState(assignment?.dueAtLocal ?? defaultDue())
  const [maxPoints, setMaxPoints] = useState(assignment?.maxPoints ? String(assignment.maxPoints) : '20')
  const [acceptLate, setAcceptLate] = useState(assignment?.acceptLate ?? true)
  const [files, setFiles] = useState<Attached[]>(
    assignment?.files.map((f) => ({ id: f.id, name: f.name, size: f.size })) ?? [],
  )
  const [published, setPublished] = useState(assignment?.published ?? true)
  const { errors, formError, reset, apply } = useFieldErrors(FIELDS)

  const submit = async () => {
    reset()
    try {
      await save.mutateAsync({
        id: assignment?.id,
        title,
        instructions,
        dueAt,
        maxPoints: maxPoints ? Number(maxPoints) : null,
        acceptLate,
        published,
        fileIds: files.map((f) => f.id),
      })
      toast.success(published ? t('Assignment published') : t('Saved as a draft'))
      onOpenChange(false)
    } catch (e) {
      apply(e)
    }
  }

  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      size="lg"
      icon={<ClipboardPen />}
      title={assignment ? t('Edit assignment') : t('New assignment')}
      description={t('Students hand in their work online before the due date.')}
      dismissible={!save.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={save.isPending}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => void submit()} loading={save.isPending}>
            {published ? t('Publish') : t('Save draft')}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {formError ? (
          <Alert tone="danger" title={t('The assignment was not saved')}>
            {formError}
          </Alert>
        ) : null}
        <FormField label={t('Title')} required error={errors.title}>
          <Input value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
        </FormField>
        <FormField
          label={t('Instructions')}
          required
          error={errors.instructions}
          hint={t('Links written in the text become clickable.')}
        >
          <Textarea rows={6} value={instructions} onChange={(e) => setInstructions(e.target.value)} />
        </FormField>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label={t('Due')} required error={errors.dueAt} hint={t('In the school’s time zone.')}>
            <Input type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
          </FormField>
          <FormField
            label={t('Marked out of')}
            error={errors.maxPoints}
            hint={t('Leave empty for feedback only.')}
          >
            <Input
              type="number"
              min={1}
              max={1000}
              value={maxPoints}
              onChange={(e) => setMaxPoints(e.target.value)}
            />
          </FormField>
        </div>
        <FormField label={t('Files')} error={errors.fileIds}>
          <AttachmentField value={files} onChange={setFiles} disabled={save.isPending} />
        </FormField>
        <div className="space-y-2">
          <ToggleRow
            label={t('Accept late work')}
            hint={t('After the due date, work is still accepted and marked late.')}
            checked={acceptLate}
            onCheckedChange={setAcceptLate}
          />
          <ToggleRow
            label={t('Published')}
            hint={t('Off: only teachers of the course can see it.')}
            checked={published}
            onCheckedChange={setPublished}
          />
        </div>
      </div>
    </Dialog>
  )
}
